import { colors } from '@globals/themes/defaultTheme'
import { AwardType, PointsBankBalance } from '@kudos/http-client'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Selectable } from '@shared/filters/selections'
import ErrorText from '@shared/forms/ErrorText'
import NumberField from '@shared/forms/NumberField'
import QualitiesAutoCompleteField from '@shared/forms/QualitiesAutoCompleteField'
import RichTextEditorField from '@shared/forms/richTextEditorField/RichTextEditorField'
import { MAX_MESSAGE_CHARS } from '@shared/wallPost/constants'
import { T, useT } from '@transifex/react'
import { ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import AwardsPointsRecipientAmountCard from './AwardsPointsRecipientAmountCard'

type AwardPreview = {
  recipientCount: number
  pointsRecipientCount: number
  totalSpend: number
  pointsBankBalance: PointsBankBalance | null
}

type AwardFormContentProps = {
  header: ReactNode
  sendToSection: ReactNode
  awardType: AwardType
  preview: AwardPreview
  qualities: Selectable[]
}
const AwardFormContent: React.FC<AwardFormContentProps> = ({
  header,
  sendToSection,
  awardType,
  preview,
  qualities,
}) => {
  const t = useT()
  const { control, setValue, formState } = useFormContext()
  const amount = useWatch({ control, name: 'amount' })
  const { recipientCount, pointsRecipientCount, totalSpend, pointsBankBalance } = preview
  // NOTE: We want to add extra padding for cases where there is minimal
  // content in the Award form. In such cases the emoji picker is not
  // scrollable i.e. restricted by dialog height so padding helps fix that.
  const isPaddingRequired = !awardType.qualitiesEnabled && !(amount > 0)

  return (
    <Stack>
      {header}
      {sendToSection}
      <Box sx={{ pb: { lg: isPaddingRequired ? 10 : undefined } }}>
        <RichTextEditorField
          name="message"
          control={control}
          placeholder={t(
            'Share a few words about their achievement and why they’ve earned this award',
          )}
          charCounterMax={MAX_MESSAGE_CHARS}
          label={<T _str="Message" />}
          isFullVersion={false}
          areMentionsSupported
        />
      </Box>
      {awardType.qualitiesEnabled && (
        <QualitiesAutoCompleteField options={qualities ?? []} control={control} name="qualities" />
      )}
      {!awardType.pointsLocked && !!awardType.budgetItemId && (
        <Box mb={2}>
          <NumberField
            fullWidth
            width={180}
            label={<T _str="Points per recipient" />}
            name="amount"
            control={control}
            value={amount ?? ''}
            onChange={event => {
              setValue('amount', parseInt(event.target.value, 10) || 0, { shouldValidate: true })
            }}
            isAllowed={input => Number(input.value) >= 0}
            decimalScale={0}
          />
        </Box>
      )}
      {/* Even though points amount is hidden we still need to show errors. */}
      {awardType.pointsLocked && formState.errors.amount?.message && (
        <ErrorText>{formState.errors.amount?.message as string}</ErrorText>
      )}
      {recipientCount > pointsRecipientCount && awardType.points > 0 && amount > 0 && (
        <Stack useFlexGap direction="row" mt={1} spacing={2}>
          <InfoOutlinedIcon sx={{ color: colors.bluePrimary }} />
          <Typography>
            <T
              _str="{ineligible} {individuals} won't receive points with their award because of your organization's eligibility rules"
              ineligible={<b>{recipientCount - pointsRecipientCount}</b>}
              individuals={
                <b>
                  <span>
                    <T
                      _str="{ineligible, plural, one {individual} other {individuals}}"
                      ineligible={recipientCount - pointsRecipientCount}
                    />
                  </span>
                </b>
              }
            />
          </Typography>
        </Stack>
      )}
      {awardType && amount > 0 && (
        <AwardsPointsRecipientAmountCard
          pointsAmount={amount}
          pointsRecipientCount={pointsRecipientCount}
          totalSpend={totalSpend}
          pointsBankAvailableBalance={pointsBankBalance?.available ?? 0}
        />
      )}
    </Stack>
  )
}

export default AwardFormContent
